import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getSymbolGroups, createBasketRun, getBasketRun } from '../../api';
import Button from '../../components/Button';
import Spinner from '../../components/Spinner';

// Columns for the per-symbol highlights grid. `better` drives best-cell
// highlighting; `get` reads from a child (BacktestSummarySerializer) row.
const COLUMNS = [
  { key: 'return_pct', label: 'Return', unit: 'pct', better: 'higher', get: (c) => num(c.return_pct) },
  { key: 'sharpe_ratio', label: 'Sharpe', unit: 'num', better: 'higher', get: (c) => num(c.sharpe_ratio) },
  { key: 'max_drawdown_pct', label: 'Max DD', unit: 'pct', better: 'lower', get: (c) => num(c.max_drawdown_pct) },
  { key: 'win_rate', label: 'Win Rate', unit: 'pct', better: 'higher', get: winRate },
  { key: 'total_trades', label: 'Trades', unit: 'int', better: null, get: (c) => num(c.total_trades) },
];

function num(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function winRate(c) {
  const w = num(c.won_trades) ?? 0;
  const l = num(c.lost_trades) ?? 0;
  const t = w + l;
  return t ? Math.round((1000 * w) / t) / 10 : null;
}

function fmtCell(unit, v) {
  if (v == null) return '—';
  if (unit === 'pct') return `${Math.round(v * 100) / 100}%`;
  if (unit === 'int') return String(Math.round(v));
  return String(Math.round(v * 1000) / 1000);
}

export default function BasketPanel({ backtest }) {
  const strategyId = backtest?.strategy_id || backtest?.strategy?.id || backtest?.strategy;

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [run, setRun] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('return_pct');
  const [sortDir, setSortDir] = useState('desc');
  const pollRef = useRef(null);

  useEffect(() => {
    getSymbolGroups()
      .then((res) => {
        const gs = res.data || [];
        setGroups(gs);
        if (gs.length) setSelectedGroup(gs[0].id);
      })
      .catch(() => setError('Could not load your watchlists.'))
      .finally(() => setGroupsLoading(false));
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // Poll the run until it finishes.
  useEffect(() => {
    if (!run?.id) return undefined;
    if (run.status === 'completed' || run.status === 'failed') return undefined;
    pollRef.current = setInterval(() => {
      getBasketRun(run.id)
        .then((res) => {
          setRun(res.data);
          if (res.data.status === 'completed' || res.data.status === 'failed') stopPolling();
        })
        .catch(() => {});
    }, 3000);
    return stopPolling;
  }, [run?.id, run?.status, stopPolling]);

  const handleRun = async () => {
    if (!selectedGroup || !strategyId) return;
    setLaunching(true);
    setError('');
    try {
      const { data } = await createBasketRun({
        strategy: strategyId,
        group: selectedGroup,
        asset_type: backtest.asset_type,
        start_date: backtest.start_date,
        end_date: backtest.end_date,
        initial_cash: backtest.initial_cash,
        slippage_perc: backtest.slippage_perc,
        commission_perc: backtest.commission_perc,
        fill_on_close: backtest.fill_on_close,
        fx_interval: backtest.fx_interval,
      });
      setRun(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start the basket run.');
    } finally {
      setLaunching(false);
    }
  };

  const children = run?.children || [];
  const done = run && (run.status === 'completed' || run.status === 'failed');

  // Best value per column, across completed legs, for highlighting.
  const best = {};
  COLUMNS.forEach((col) => {
    if (!col.better) return;
    const vals = children.map(col.get).filter((v) => v != null);
    if (!vals.length) return;
    best[col.key] = col.better === 'higher' ? Math.max(...vals) : Math.min(...vals);
  });

  const sortedChildren = [...children].sort((a, b) => {
    const col = COLUMNS.find((c) => c.key === sortKey);
    const av = col ? col.get(a) : null;
    const bv = col ? col.get(b) : null;
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const selectedGroupObj = groups.find((g) => g.id === selectedGroup);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        background: 'var(--panel-2, rgba(255,255,255,0.04))',
        border: '1px solid var(--border, rgba(255,255,255,0.08))',
        borderRadius: 12, padding: 16, marginBottom: 16,
      }}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 16 }}>Run across a basket</h3>
        <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: 13 }}>
          Runs this strategy over every symbol in a basket, using the current dates and settings
          ({backtest.start_date} → {backtest.end_date}). Each symbol becomes its own full backtest.
        </p>

        {groupsLoading ? (
          <Spinner />
        ) : groups.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>
            You don't have any watchlists yet. Create one on the <Link to="/watchlists">Watchlists</Link> page.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              aria-label="Choose a basket"
              style={{
                background: 'var(--bg, #0b0f14)',
                border: '1px solid var(--border, rgba(255,255,255,0.12))', borderRadius: 8,
                color: 'var(--text)', padding: '8px 10px', fontSize: 14, minWidth: 180,
              }}
            >
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({(g.symbols || []).length})
                </option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={handleRun}
              loading={launching}
              disabled={!selectedGroupObj || (selectedGroupObj.symbols || []).length === 0}
            >
              Run “{selectedGroupObj?.name}” across this strategy
            </Button>
            <Link to="/watchlists" style={{ fontSize: 13, color: 'var(--muted)' }}>Manage watchlists</Link>
          </div>
        )}
        {error && <div style={{ marginTop: 10, color: 'var(--red)', fontSize: 13 }}>{error}</div>}
      </div>

      {run && (
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12,
          }}>
            {!done && <Spinner size={14} />}
            <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>
              {done ? 'Complete' : 'Running'}: {run.completed_count}/{run.total} symbols
            </span>
            <div style={{
              flex: 1, minWidth: 120, height: 6, background: 'rgba(255,255,255,0.08)',
              borderRadius: 3, overflow: 'hidden', maxWidth: 260,
            }}>
              <div style={{
                width: `${run.total ? (100 * run.completed_count) / run.total : 0}%`,
                height: '100%', background: 'var(--teal, #00d4aa)', transition: 'width .3s',
              }} />
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  <th style={thStyle}>Symbol</th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      style={{ ...thStyle, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {col.label}{sortKey === col.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </th>
                  ))}
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedChildren.map((c) => {
                  const failed = c.status === 'failed';
                  return (
                    <tr key={c.id} style={{ borderTop: '1px solid var(--border, rgba(255,255,255,0.08))' }}>
                      <td style={tdStyle}>
                        {c.status === 'completed' ? (
                          <Link to={`/backtests/${c.id}`} style={{ color: 'var(--teal, #00d4aa)', fontWeight: 700 }}>
                            {c.symbol}
                          </Link>
                        ) : (
                          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{c.symbol}</span>
                        )}
                      </td>
                      {COLUMNS.map((col) => {
                        const v = col.get(c);
                        const isBest = col.better && v != null && best[col.key] === v && children.length > 1;
                        return (
                          <td key={col.key} style={{
                            ...tdStyle,
                            color: isBest ? 'var(--teal, #00d4aa)' : 'var(--text)',
                            fontWeight: isBest ? 700 : 400,
                          }}>
                            {c.status === 'completed' ? fmtCell(col.unit, v) : '—'}
                          </td>
                        );
                      })}
                      <td style={tdStyle}>
                        <StatusPill status={c.status} />
                        {failed && c.results?.error && (
                          <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 2, maxWidth: 220 }}>
                            {String(c.results.error).slice(0, 80)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {done && (
            <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 12 }}>
              Click a symbol to open its full backtest results.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: '8px 10px', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.3 };
const tdStyle = { padding: '8px 10px' };

function StatusPill({ status }) {
  const map = {
    completed: { bg: 'rgba(47,214,195,0.15)', color: '#2fd6c3' },
    pending: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    running: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    failed: { bg: 'rgba(248,81,73,0.15)', color: '#f85149' },
  };
  const c = map[status] || map.pending;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.color, textTransform: 'capitalize',
    }}>{status}</span>
  );
}
